import { GetCounterpartiesResponseRowDto } from "@common/dto/counterparty/get-counterparties-response.dto";
import { NoCounterpartyTotals } from "@common/schemas/counterparty.schema";
import {
    displayAccountLabel,
    preferCodeText,
} from "@common/helpers/accounting";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";

interface BuildCounterpartyDirectoryRowsParams {
    counterparties: GetCounterpartiesResponseRowDto[];
    noCounterpartyTotals: NoCounterpartyTotals | null | undefined;
    showAccountNumbers: boolean;
}

export function buildCounterpartyDirectoryRows({
    counterparties,
    noCounterpartyTotals,
    showAccountNumbers,
}: BuildCounterpartyDirectoryRowsParams): CounterpartyDirectoryViewRow[] {
    const rows: CounterpartyDirectoryViewRow[] = counterparties.map((row) => {
        const children: CounterpartyDirectoryViewRow[] = (
            row.usageStats?.accountBreakdown ?? []
        ).map((entry) => ({
            id: `${row.counterparty.id}-${entry.accountId}`,
            name: displayAccountLabel(
                {
                    codeText: preferCodeText(
                        entry.accountCodeText,
                        entry.accountCode,
                    ),
                    name: entry.accountName,
                },
                showAccountNumbers,
            ),
            accountId: entry.accountId,
            accountCode: entry.accountCode,
            counterparty: row.counterparty,
            debit: entry.debit,
            credit: entry.credit,
            journalEntryLinesCount: entry.journalEntryLinesCount,
            isNoCounterparty: false,
            isAccountRow: true,
        }));

        return {
            id: row.counterparty.id,
            name: row.counterparty.name,
            counterparty: row.counterparty,
            fieldReviews: row.fieldReviews,
            journalEntryLinesCount: row.usageStats?.journalEntryLinesCount ?? 0,
            debit: row.usageStats?.debit ?? 0,
            credit: row.usageStats?.credit ?? 0,
            billableExpenseCount: row.usageStats?.billableExpenseCount ?? 0,
            invoiceCount: row.usageStats?.invoiceCount ?? 0,
            billCount: row.usageStats?.billCount ?? 0,
            isNoCounterparty: false,
            isAccountRow: false,
            ...(children.length >= 1 ? { children } : {}),
        };
    });

    if (noCounterpartyTotals) {
        const noCounterpartyAccountChildren: CounterpartyDirectoryViewRow[] =
            noCounterpartyTotals.accountBreakdown.map((entry) => ({
                id: `no-counterparty-${entry.accountId}`,
                name: displayAccountLabel(
                    {
                        codeText: preferCodeText(
                            entry.accountCodeText,
                            entry.accountCode,
                        ),
                        name: entry.accountName,
                    },
                    showAccountNumbers,
                ),
                accountId: entry.accountId,
                accountCode: entry.accountCode,
                counterparty: null,
                debit: entry.debit,
                credit: entry.credit,
                journalEntryLinesCount: entry.journalEntryLinesCount,
                isNoCounterparty: true,
                isAccountRow: true,
            }));

        rows.push({
            id: "no-counterparty",
            name: "No counterparty",
            counterparty: null,
            journalEntryLinesCount: noCounterpartyTotals.journalEntryLinesCount,
            debit: noCounterpartyTotals.debit,
            credit: noCounterpartyTotals.credit,
            isNoCounterparty: true,
            isAccountRow: false,
            ...(noCounterpartyAccountChildren.length > 1
                ? { children: noCounterpartyAccountChildren }
                : {}),
        });
    }

    return rows;
}
